import { Controller } from "@hotwired/stimulus"

// Hex, RGB and HSL kept in step: edit any one and the other two follow.
export default class extends Controller {
  static targets = ["hex", "rgb", "hsl", "picker", "swatch", "status"]
  static values = { messages: Object }

  fromHex() {
    const m = this.hexTarget.value.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i)
    if (!m) return this.invalid()
    let h = m[1]
    if (h.length === 3) h = h.split("").map(c => c + c).join("")
    this.apply(parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16), "hex")
  }

  fromRgb() {
    const m = this.rgbTarget.value.trim().match(/^(?:rgba?\()?\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*(?:[,/].*)?\)?$/i)
    if (!m) return this.invalid()
    const [r, g, b] = m.slice(1, 4).map(Number)
    if ([r, g, b].some(v => v > 255)) return this.invalid()
    this.apply(r, g, b, "rgb")
  }

  fromHsl() {
    const m = this.hslTarget.value.trim().match(/^(?:hsla?\()?\s*(\d{1,3}(?:\.\d+)?)(?:deg)?\s*[, ]\s*(\d{1,3}(?:\.\d+)?)%?\s*[, ]\s*(\d{1,3}(?:\.\d+)?)%?\s*(?:[,/].*)?\)?$/i)
    if (!m) return this.invalid()
    const [h, s, l] = m.slice(1, 4).map(Number)
    if (h > 360 || s > 100 || l > 100) return this.invalid()
    const [r, g, b] = hslToRgb(h, s / 100, l / 100)
    this.apply(r, g, b, "hsl")
  }

  fromPicker() {
    this.hexTarget.value = this.pickerTarget.value.toUpperCase()
    this.fromHex()
  }

  apply(r, g, b, source) {
    const hex = "#" + [r, g, b].map(v => v.toString(16).padStart(2, "0")).join("").toUpperCase()
    const [h, s, l] = rgbToHsl(r, g, b)
    if (source !== "hex") this.hexTarget.value = hex
    if (source !== "rgb") this.rgbTarget.value = `rgb(${r}, ${g}, ${b})`
    if (source !== "hsl") this.hslTarget.value = `hsl(${h}, ${s}%, ${l}%)`
    this.pickerTarget.value = hex.toLowerCase()
    this.swatchTarget.style.background = hex
    this.statusTarget.textContent = this.messagesValue.valid
  }

  invalid() {
    this.swatchTarget.style.background = "transparent"
    this.statusTarget.textContent = this.messagesValue.invalid
  }
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min
  let h = 0
  if (d) {
    if (max === r) h = ((g - b) / d) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h = Math.round(h * 60)
    if (h < 0) h += 360
  }
  const l = (max + min) / 2
  const s = d ? d / (1 - Math.abs(2 * l - 1)) : 0
  return [h, Math.round(s * 100), Math.round(l * 100)]
}

function hslToRgb(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let [r, g, b] = [0, 0, 0]
  if (h < 60) [r, g, b] = [c, x, 0]
  else if (h < 120) [r, g, b] = [x, c, 0]
  else if (h < 180) [r, g, b] = [0, c, x]
  else if (h < 240) [r, g, b] = [0, x, c]
  else if (h < 300) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]
  return [r, g, b].map(v => Math.round((v + m) * 255))
}
